import { type ReactNode, useId } from 'react';
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';

import { IntakePill } from '../shell/IntakePill';
import { cardSx, type PillTone, wf } from '../shell/tokens';

import { IntakeJourneyStatus } from './IntakeJourneyStatus';
import type { IntakeServiceTier, IntakeSetupService } from './IntakeSetupServicesPage';

export const INTAKE_STATUS_TABS = ['billing', 'credentialing', 'ehr-import'] as const;
export type IntakeStatusTab = (typeof INTAKE_STATUS_TABS)[number];

export const INTAKE_STATUS_TAB_LABELS: Record<IntakeStatusTab, string> = {
  billing: 'Billing',
  credentialing: 'Credentialing',
  'ehr-import': 'EHR Migration',
};

/**
 * The services saved in Agreements & setup decide which tracks appear. Billing follows JotBill
 * or the Self-Pay tier, Credentialing follows JotCred, and EHR Migration has no election so it
 * always shows. Before anything is saved every track stays visible.
 */
export function intakeStatusTabsForServices(
  tier: IntakeServiceTier,
  services: readonly IntakeSetupService[],
): IntakeStatusTab[] {
  const billing = tier === 'self_pay' || services.includes('jotbill');
  const credentialing = services.includes('jotcred');
  if (!billing && !credentialing) return [...INTAKE_STATUS_TABS];
  return INTAKE_STATUS_TABS.filter(
    tab =>
      tab === 'ehr-import' ||
      (tab === 'billing' && billing) ||
      (tab === 'credentialing' && credentialing),
  );
}

/** Keep a stale or hand-typed selection from pointing at a track the practice did not elect. */
export function effectiveIntakeStatusTab(
  requested: IntakeStatusTab,
  available: readonly IntakeStatusTab[],
): IntakeStatusTab {
  return available.includes(requested) ? requested : (available[0] ?? 'billing');
}

/** A track that has no surface yet: the design's card, a slate pill, one line of copy. */
function ComingSoonCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ ...cardSx, padding: '26px 28px' }}>
      <IntakePill label="Coming soon" tone="slate" uppercase />
      <Typography
        component="h3"
        sx={{
          fontSize: '19px',
          fontWeight: 700,
          letterSpacing: '-0.02em',
          color: wf.ink,
          mt: 1.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{ fontSize: '14px', lineHeight: 1.65, color: '#52526a', mt: 0.75, maxWidth: 620 }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export function CredentialingComingSoon() {
  return (
    <ComingSoonCard title="Credentialing">
      Your practice’s credentialing progress will appear here.
    </ComingSoonCard>
  );
}

export function EhrImportComingSoon() {
  return (
    <ComingSoonCard title="EHR migration">
      EHR migration will be available here in a future update.
    </ComingSoonCard>
  );
}

/**
 * The track pill's tone, from the status string the journey already passes.
 * The design's count-based Billing pill ("2 need you") would need the payer
 * fold lifted into LiveIntakeJourney, which this component does not read.
 */
function trackTone(status: string): PillTone {
  const normalized = status.toLowerCase();
  // The journey passes 'Unavailable' | 'Locked' | 'View current status' today;
  // the other branches are ready for a status that carries fold counts.
  if (normalized === 'locked' || normalized === 'coming soon' || normalized === 'unavailable')
    return 'slate';
  if (normalized.includes('needs') || normalized.includes('action')) return 'amber';
  if (normalized.includes('complete') || normalized.includes('live')) return 'green';
  return 'indigo';
}

/** The status shell composes the existing service surfaces without inventing completion state. */
export function IntakeStatusDashboard({
  selectedTab,
  onSelectTab,
  billing,
  ehrImport,
  ehrImportAvailable = Boolean(ehrImport),
  credentialing,
  credentialingAvailable = Boolean(credentialing),
  billingStatus = 'View current status',
  credentialingLockedReason,
  keepPanelsMounted = false,
  availableTabs = INTAKE_STATUS_TABS,
}: {
  keepPanelsMounted?: boolean;
  selectedTab: IntakeStatusTab;
  onSelectTab: (tab: IntakeStatusTab) => void;
  billing: ReactNode;
  ehrImport?: ReactNode;
  ehrImportAvailable?: boolean;
  credentialing?: ReactNode;
  /** Availability is independent of whether the active panel is mounted. */
  credentialingAvailable?: boolean;
  billingStatus?: string;
  credentialingLockedReason?: string;
  /** Tracks the practice elected; see intakeStatusTabsForServices. Defaults to every track. */
  availableTabs?: readonly IntakeStatusTab[];
}) {
  const id = useId();
  const tabs = [
    {
      value: 'billing',
      label: INTAKE_STATUS_TAB_LABELS.billing,
      caption: 'Your payer connections',
      status: billingStatus,
    },
    {
      value: 'credentialing',
      label: INTAKE_STATUS_TAB_LABELS.credentialing,
      caption: credentialingLockedReason ?? 'Credentialing for your practice',
      status: credentialingLockedReason
        ? 'Locked'
        : credentialingAvailable
          ? 'View current status'
          : 'Coming soon',
    },
    {
      value: 'ehr-import',
      label: INTAKE_STATUS_TAB_LABELS['ehr-import'],
      caption: 'Bring your patient records',
      status: ehrImportAvailable ? 'View current status' : 'Coming soon',
    },
  ].filter(tab => availableTabs.includes(tab.value as IntakeStatusTab));
  const activeTab = effectiveIntakeStatusTab(selectedTab, availableTabs);
  return (
    <Stack spacing={3}>
      <Tabs
        value={activeTab}
        onChange={(_, value: IntakeStatusTab) => onSelectTab(value)}
        aria-label="Intake Status services"
        variant="fullWidth"
        sx={{
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTabs-list': {
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: `repeat(${tabs.length}, minmax(0, 1fr))` },
            gap: '10px',
          },
          '& .MuiTab-root': { minWidth: 0, maxWidth: 'none' },
        }}
      >
        {tabs.map(tab => (
          <Tab
            key={tab.value}
            value={tab.value}
            id={`${id}-${tab.value}-tab`}
            aria-controls={`${id}-${tab.value}-panel`}
            sx={{
              ...cardSx,
              padding: '14px 16px',
              borderColor: activeTab === tab.value ? wf.blue : wf.line,
              boxShadow: activeTab === tab.value ? `0 0 0 1px ${wf.blue} inset` : 'none',
              textAlign: 'left',
              textTransform: 'none',
              alignItems: 'stretch',
              opacity: 1,
            }}
            label={
              <Stack spacing={0.625}>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography
                    component="span"
                    sx={{ fontSize: '14px', fontWeight: 700, color: wf.ink }}
                  >
                    {tab.label}
                  </Typography>
                  <IntakePill label={tab.status} tone={trackTone(tab.status)} />
                </Stack>
                <Typography
                  component="span"
                  sx={{ fontSize: '12px', lineHeight: 1.5, color: wf.muted, textAlign: 'left' }}
                >
                  {tab.caption}
                </Typography>
              </Stack>
            }
          />
        ))}
      </Tabs>
      {tabs
        .filter(tab => keepPanelsMounted || tab.value === activeTab)
        .map(tab => (
          <Box
            key={tab.value}
            role="tabpanel"
            hidden={tab.value !== activeTab}
            inert={tab.value !== activeTab}
            id={`${id}-${tab.value}-panel`}
            aria-labelledby={`${id}-${tab.value}-tab`}
          >
            {tab.value === 'billing' ? (
              billing
            ) : tab.value === 'credentialing' ? (
              credentialingLockedReason ? (
                <IntakeJourneyStatus title="Credentialing" statusLabel="Locked">
                  <Typography>{credentialingLockedReason}</Typography>
                </IntakeJourneyStatus>
              ) : (
                (credentialing ?? <CredentialingComingSoon />)
              )
            ) : (
              (ehrImport ?? <EhrImportComingSoon />)
            )}
          </Box>
        ))}
    </Stack>
  );
}
